import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { pill, LABEL, MUTED } from '../lib/equipment';
import { STATUS_TONE, AuthedImg, Swatch, fmtYmd } from '../components/TechPackParts';
import '../components/techpacks.css';
// @@TECH_PACK_THUMBS_v97@@ cards load the cached 480 px preview (?thumb=sm), never the original

// @@TECH_PACKS_v94@@ HQ › Tech Packs — list + house Pantone ink library.
// Reads: every internal role. Writes: admin/manager/staff (server re-checks).
const JSON_HEADERS = { 'Content-Type': 'application/json' };
async function readJson(r) { try { return await r.json(); } catch (e) { return {}; } }
const FILTERS = [['', 'All active'], ['draft', 'Drafts'], ['review', 'Awaiting client'], ['changes_requested', 'Changes requested'], ['approved', 'Approved'], ['archived', 'Archived']];

export default function TechPacks() {
  const { user } = useAuth();
  const canWrite = !!user && ['admin', 'manager', 'staff'].includes(user.role);
  const canManage = !!user && ['admin', 'manager'].includes(user.role);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'inks' ? 'inks' : 'packs';
  const [msg, setMsg] = useState('');
  const tabBtn = (key, label) => (
    <button key={key} type="button" role="tab" aria-selected={tab === key}
      className={tab === key ? 'tp-tab tp-tab-on' : 'tp-tab'}
      onClick={() => { setMsg(''); setParams(key === 'packs' ? {} : { tab: key }, { replace: true }); }}>{label}</button>
  );
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Tech Packs</h1>
        <p>Screen-print specs: blank, print locations, Pantone inks, mesh and placement. Clients review them in their portal; printers get one download link.</p>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}
      <div role="tablist" className="tp-tabs">
        {tabBtn('packs', 'Tech packs')}
        {tabBtn('inks', 'Ink library')}
      </div>
      {tab === 'packs' && <PacksPanel canWrite={canWrite} onMsg={setMsg} />}
      {tab === 'inks' && <InkLibrary canWrite={canWrite} canManage={canManage} onMsg={setMsg} />}
    </div>
  );
}

function PacksPanel({ canWrite, onMsg }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [packs, setPacks] = useState(null);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState({ title: '', client_id: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQ(qLive.trim()), 250); return () => clearTimeout(t); }, [qLive]);
  useEffect(() => {
    let on = true;
    setPacks(null);
    const sp = new URLSearchParams();
    if (filter === 'archived') sp.set('archived', '1'); else if (filter) sp.set('status', filter);
    if (q) sp.set('q', q);
    apiFetch('/api/techpacks' + (sp.toString() ? '?' + sp : ''))
      .then(async (r) => { const d = await readJson(r); if (!on) return; if (r.ok) setPacks(d.packs || []); else { setPacks([]); onMsg(d.error || 'Could not load tech packs.'); } })
      .catch(() => on && setPacks([]));
    return () => { on = false; };
  }, [filter, q, onMsg]);
  useEffect(() => {
    apiFetch('/api/techpacks/clients').then(readJson).then((d) => setClients(d.clients || [])).catch(() => {});
  }, []);

  async function create(e) {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setBusy(true);
    const r = await apiFetch('/api/techpacks', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(draft) });
    const d = await readJson(r);
    setBusy(false);
    if (r.ok) navigate('/tech-packs/' + d.pack.id); else onMsg(d.error || 'Could not create the tech pack.');
  }

  return (
    <>
      {canWrite && (
        <form className="hq-panel" style={{ marginBottom: 16 }} onSubmit={create}>
          <div className="hq-panel-head"><h2>New tech pack</h2></div>
          <div className="tp-row" style={{ padding: 20 }}>
            <label style={{ flex: '2 1 260px' }}><span style={LABEL}>Title</span>
              <input className="hq-input tp-w100" value={draft.title} maxLength={160} placeholder="e.g. Fall Tour 2026 — Skull Tee"
                onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </label>
            <label style={{ flex: '1 1 220px' }}><span style={LABEL}>Client (optional)</span>
              <select className="hq-select tp-w100" value={draft.client_id} onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}>
                <option value="">No client — internal / stock</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.designation_code ? ` (${c.designation_code})` : ''}</option>)}
              </select>
            </label>
            <button className="hq-btn" type="submit" disabled={!draft.title.trim() || busy}>Create</button>
          </div>
        </form>
      )}
      <div className="hq-panel">
        <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <select className="hq-select" aria-label="Filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="hq-input" style={{ flex: '1 1 220px', maxWidth: 360 }} placeholder="Search title, client, style #, TP number…"
            value={qLive} onChange={(e) => setQLive(e.target.value)} />
        </div>
        {packs === null && <div className="hq-empty">Loading…</div>}
        {packs && packs.length === 0 && <div className="hq-empty">No tech packs here yet.</div>}
        {packs && packs.length > 0 && (
          <div className="tp-grid">
            {packs.map((p) => (
              <button type="button" key={p.id} className="tp-card" onClick={() => navigate('/tech-packs/' + p.id)}>
                <div className="tp-card-thumb">
                  {p.thumb_id
                    ? <AuthedImg path={`/api/techpacks/${p.id}/files/${p.thumb_id}?thumb=sm`} alt="" className="tp-card-img" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <span className="tp-card-ph">No mockup</span>}
                </div>
                <div className="tp-card-body">
                  <div className="tp-mono">{p.code} · R{p.revision}</div>
                  <div className="tp-card-title">{p.title}</div>
                  <div style={MUTED}>{p.client_name || 'No client'}{p.client_style_code ? ` · Style ${p.client_style_code}` : ''}</div>
                  <div style={{ ...MUTED, marginTop: 2 }}>{[p.blank, p.blank_color].filter(Boolean).join(' · ') || 'Blank not set'}</div>
                  <div className="tp-card-foot">
                    <span style={pill(STATUS_TONE[p.status])}>{p.status_label}</span>
                    <span style={MUTED}>{p.locations} loc{p.locations === 1 ? '' : 's'}{p.due_date ? ' · due ' + fmtYmd(p.due_date) : ''}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const BLANK_INK = { pantone: '', name: '', hex: '#000000', ink_type: '', notes: '', sort: 100 };
function InkLibrary({ canWrite, canManage, onMsg }) {
  const [inks, setInks] = useState(null);
  const [form, setForm] = useState(BLANK_INK);
  const [editId, setEditId] = useState('');
  const [edit, setEdit] = useState(BLANK_INK);
  const [meta, setMeta] = useState(null);
  const load = useCallback(() => {
    apiFetch('/api/techpacks/inks').then(async (r) => { const d = await readJson(r); if (r.ok) setInks(d.inks || []); else { setInks([]); onMsg(d.error || 'Could not load inks.'); } }).catch(() => setInks([]));
  }, [onMsg]);
  useEffect(() => { load(); apiFetch('/api/techpacks/meta').then(readJson).then(setMeta).catch(() => {}); }, [load]);

  async function save(body, id) {
    const r = await apiFetch('/api/techpacks/inks' + (id ? '/' + id : ''), { method: id ? 'PUT' : 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
    const d = await readJson(r);
    if (!r.ok) { onMsg(d.error || 'Could not save the ink.'); return false; }
    onMsg(''); load(); return true;
  }
  async function remove(id) {
    if (!window.confirm('Remove this ink from the library? Packs that already use it keep their colors.')) return;
    const r = await apiFetch('/api/techpacks/inks/' + id, { method: 'DELETE' });
    if (r.ok) load(); else onMsg('Could not remove the ink.');
  }
  const inkTypes = (meta && meta.inks) || [];

  return (
    <div className="hq-panel">
      <div className="hq-panel-head"><h2>House ink library</h2>
        <span style={MUTED}>Swatches are on-screen approximations — always match to a physical Pantone guide.</span></div>
      {canWrite && (
        <form className="tp-row" style={{ padding: '16px 20px', borderBottom: '1px solid var(--hq-line)' }}
          onSubmit={async (e) => { e.preventDefault(); if (await save(form)) setForm(BLANK_INK); }}>
          <label><span style={LABEL}>Swatch</span><input type="color" className="tp-color" value={form.hex || '#000000'} onChange={(e) => setForm({ ...form, hex: e.target.value })} /></label>
          <label style={{ flex: '1 1 140px' }}><span style={LABEL}>Pantone</span><input className="hq-input tp-w100" placeholder="186 C" value={form.pantone} onChange={(e) => setForm({ ...form, pantone: e.target.value })} /></label>
          <label style={{ flex: '1 1 160px' }}><span style={LABEL}>Name</span><input className="hq-input tp-w100" placeholder="Cardinal Red" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label style={{ flex: '1 1 150px' }}><span style={LABEL}>Ink type</span>
            <select className="hq-select tp-w100" value={form.ink_type} onChange={(e) => setForm({ ...form, ink_type: e.target.value })}>
              <option value="">Any</option>{inkTypes.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label style={{ flex: '2 1 200px' }}><span style={LABEL}>Notes</span><input className="hq-input tp-w100" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <button className="hq-btn" type="submit" disabled={!form.pantone.trim() && !form.name.trim()}>Add ink</button>
        </form>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="hq-table" style={{ minWidth: 640 }}>
          <thead><tr><th style={{ width: 44 }} /><th>Pantone</th><th>Name</th><th>Hex</th><th>Ink type</th><th>Notes</th><th /></tr></thead>
          <tbody>
            {(inks || []).map((k) => (editId === k.id ? (
              <tr key={k.id}>
                <td><input type="color" className="tp-color" value={edit.hex || '#000000'} onChange={(e) => setEdit({ ...edit, hex: e.target.value })} /></td>
                <td><input className="hq-input tp-w100" value={edit.pantone || ''} onChange={(e) => setEdit({ ...edit, pantone: e.target.value })} /></td>
                <td><input className="hq-input tp-w100" value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></td>
                <td className="tp-mono">{edit.hex}</td>
                <td><select className="hq-select" value={edit.ink_type || ''} onChange={(e) => setEdit({ ...edit, ink_type: e.target.value })}>
                  <option value="">Any</option>{inkTypes.map(([kk, l]) => <option key={kk} value={kk}>{l}</option>)}</select></td>
                <td><input className="hq-input tp-w100" value={edit.notes || ''} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="hq-btn" onClick={async () => { if (await save(edit, k.id)) setEditId(''); }}>Save</button>{' '}
                  <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setEditId('')}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={k.id}>
                <td><Swatch hex={k.hex} size={22} /></td>
                <td className="tp-mono" style={{ fontWeight: 600 }}>{k.pantone ? 'PMS ' + k.pantone : '—'}</td>
                <td>{k.name}</td>
                <td className="tp-mono" style={MUTED}>{k.hex || '—'}</td>
                <td style={MUTED}>{(inkTypes.find((x) => x[0] === k.ink_type) || [])[1] || 'Any'}</td>
                <td style={MUTED}>{k.notes || ''}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {canWrite && <button type="button" className="hq-linkish" onClick={() => { setEditId(k.id); setEdit({ ...BLANK_INK, ...k, pantone: k.pantone || '', notes: k.notes || '', ink_type: k.ink_type || '', hex: k.hex || '#000000' }); }}>Edit</button>}
                  {canManage && <button type="button" className="hq-link-danger" style={{ marginLeft: 12 }} onClick={() => remove(k.id)}>Remove</button>}
                </td>
              </tr>
            )))}
            {inks && inks.length === 0 && <tr><td className="hq-empty" colSpan={7}>No inks yet.</td></tr>}
            {inks === null && <tr><td className="hq-empty" colSpan={7}>Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
